import { useEffect } from 'react';

import { Constants } from '@constants';
import { numbersOnly } from '@forms/helpers/normalisers';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleLeft } from '@iwoca/icons';
import { PostApplicationRequestBody } from '@iwoca/lapi-client/edge.js';
import { Button, Card } from '@iwoca/orion-ui';
import { RootState } from '@iwProvider/configureStore';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { reduxForm, SubmissionError, InjectedFormProps } from 'redux-form';

import { FormField } from './tempFormFields/FormField.js';
import { FormFieldSelect } from './tempFormFields/Select.js';
import * as multiLoanselectors from '../../multiLoanDashboard/dashboardWidgets/tabularFlexiDashboard/multiLoanDashboardSelector';
import * as selectors from '../applyFlexiLoanSelectors';
import {
    APPLY_FLEXI_LOAN_SUBMIT_APPLICATION,
    CREATE_APPLICATION
} from '../ApplyFlexiLoanView.actions';
import applyFlexiLoanFormValidation from './ApplyFlexiLoanFormValidation';
import styles from './ApplyFlexiLoanFormView.module.css';
import ApprovalRequirements from './ApprovalRequirements/ApprovalRequirements';
import { loanDurationOptionsDE } from './loanDurationOptionsDE';
import loanPurposeOptions from './loanPurposeOptions.json';
import loanPurposeOptionsDE from './loanPurposeOptionsDE.json';
import { NEW_IwLink } from '../../../iwRouter/NEW_IwLink';

export type TFormData = {
    amount: number;
    turnover: string;
    purpose: NonNullable<
        NonNullable<
            NonNullable<PostApplicationRequestBody>['data']
        >['requested_products'][number]['purpose']
    >;
    duration?:
        | 'few_months'
        | 'ongoing'
        | 'one_to_two_years'
        | 'three_plus_years';
};

const formConfiguration = {
    form: 'ApplyForm',
    enableReinitialize: true,
    touchOnChange: true,
    validate: (values: TFormData) => applyFlexiLoanFormValidation(values)
};

const mapStateToProps = (state: RootState) => {
    const flexiAccounts = _.filter(
        state.api.accounts?.data?.accounts,
        (account) => account.product_type !== 'merchant_cash_advance'
    );

    const ongoingFlexiLoan = _.find(
        flexiAccounts,
        (account) => account.loan_status === 'ongoing'
    );

    const currentPrincipal = ongoingFlexiLoan?.balance?.principal;

    return {
        createApplicationLoading:
            state?.applyFlexiLoanView?.createApplicationLoading!,
        documentRequirements: multiLoanselectors.getDocumentRequirements(state),
        isBankStatementsRequired:
            multiLoanselectors.isFlexiLoanBankStatementRequired(state),
        initialValues: {
            amount: undefined,
            purpose: undefined,
            turnover: selectors.getCompanyAnnualTurnover(state),
            duration: undefined
        },
        formAmountValue: state?.form?.ApplyForm?.values?.amount,
        currentCreditLimit: ongoingFlexiLoan?.max_approved_loan_amount,
        currentPrincipal: currentPrincipal,
        formStep: state.applyFlexiLoanView.formStep,
        applicationId: state?.api?.application?.data?.application_id
    };
};

export type TDocumentRequirements = ReturnType<
    typeof multiLoanselectors.getDocumentRequirements
>;

const mapDispatchToProps = (dispatch: Dispatch) => ({
    createApplication: (values: TFormData) =>
        dispatch({
            type: CREATE_APPLICATION,
            values
        }),
    submitFlexiLoanApplication: () =>
        dispatch({
            type: APPLY_FLEXI_LOAN_SUBMIT_APPLICATION
        })
});

const validateDocRequirements = (
    bankStatementRequired: boolean,
    docRequirements: TDocumentRequirements
) => {
    if (bankStatementRequired) {
        throw new SubmissionError({
            error: 'isMissingBankStatementRequired'
        });
    }

    _.forEach(docRequirements, ({ status, hasError, type }) => {
        if (status === 'required' || hasError) {
            throw new SubmissionError({
                error: 'isMissingDocumentRequirements',
                message: `${type} is missing`
            });
        }
    });
};

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

type TApplyFlexiLoanFormViewProps = PropsFromRedux &
    InjectedFormProps<TFormData>;

const ApplyFlexiLoanFormView = ({
    handleSubmit,
    submitFlexiLoanApplication,
    submitFailed,
    isBankStatementsRequired,
    documentRequirements,
    createApplication,
    createApplicationLoading,
    formAmountValue,
    currentCreditLimit,
    currentPrincipal,
    formStep,
    applicationId
}: TApplyFlexiLoanFormViewProps) => {
    return (
        <div>
            {formStep === 'createApplication' ? (
                <form
                    onSubmit={handleSubmit((values) => {
                        createApplication(values);
                    })}
                >
                    <CreateApplicationStep
                        formAmountValue={formAmountValue}
                        currentPrincipal={currentPrincipal}
                        currentCreditLimit={currentCreditLimit}
                        createApplicationLoading={createApplicationLoading!}
                    />
                </form>
            ) : (
                <form
                    onSubmit={handleSubmit(() => {
                        validateDocRequirements(
                            isBankStatementsRequired,
                            documentRequirements
                        );

                        submitFlexiLoanApplication();
                    })}
                >
                    <UploadDocumentsStep
                        submitFailed={submitFailed}
                        documentRequirements={documentRequirements}
                        applicationId={applicationId}
                    />
                </form>
            )}
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
    // @ts-expect-error The mapDispatchToProps overrides some redux-form props.
)(reduxForm(formConfiguration)(ApplyFlexiLoanFormView));

const CreateApplicationStep = ({
    formAmountValue,
    currentPrincipal,
    currentCreditLimit,
    createApplicationLoading
}: {
    formAmountValue: number;
    currentPrincipal: number;
    currentCreditLimit: number;
    createApplicationLoading: boolean;
}) => {
    const intl = useIntl();

    const isTopup = getQueryParameter(getCurrentLocation(), 'topup');

    useEffect(() => {
        if (isTopup) {
            tracking.ampli.topupReapplyFormApplicationStepViewed();
        } else {
            tracking.ampli.newLoanApplicationStepViewed();
        }
    }, [isTopup]);

    return (
        <div>
            <NEW_IwLink
                className={styles.backBtn}
                to={{
                    pathname: '/'
                }}
            >
                <IconAngleLeft />
                <FormattedMessage
                    id="_.apply_flexi_loan.back_button"
                    defaultMessage="Go Back"
                />
            </NEW_IwLink>
            <Card>
                <Card.Header>
                    <Card.Title>
                        {isTopup ? (
                            <FormattedMessage
                                id="_.apply_topup_loan.title"
                                defaultMessage="Request more funds"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.reapply_loan.title"
                                defaultMessage="Apply for another loan"
                            />
                        )}
                    </Card.Title>
                </Card.Header>

                <Card.Content>
                    <FormField
                        name="amount"
                        label={
                            isTopup ? (
                                <FormattedMessage
                                    id="_.apply_flexi_loan.topupRequestedAmount.label"
                                    defaultMessage="How much more would you like to borrow?"
                                />
                            ) : (
                                <FormattedMessage
                                    id="_.apply_flexi_loan.requestedAmount.label"
                                    defaultMessage="How much do you need?"
                                />
                            )
                        }
                        normalize={(value: string) =>
                            parseInt(numbersOnly(value), 10)
                        }
                        format={(value: number) =>
                            value
                                ? intlFormatCurrency({
                                      intl,
                                      value
                                  })
                                : ''
                        }
                        placeholder={intl.formatMessage(
                            {
                                id: '_.apply_flexi_loan.requestedAmount.placeholder',
                                defaultMessage:
                                    'Enter an amount between {minAmount} and {maxAmount}'
                            },
                            {
                                minAmount: intlFormatCurrency({
                                    intl,
                                    value: Constants.MIN_LOAN_AMOUNT
                                }),
                                maxAmount: intlFormatCurrency({
                                    intl,
                                    value: Constants.MAX_LOAN_AMOUNT
                                })
                            }
                        )}
                    />

                    {isTopup &&
                        !!formAmountValue &&
                        formAmountValue <=
                            Constants.MAX_LOAN_AMOUNT - currentPrincipal &&
                        // If new credit limit (inputted amount + currentPrincipal) is more than current
                        // credit limit show new credit limit message, else show current credit limit message
                        (formAmountValue + currentPrincipal >
                        currentCreditLimit ? (
                            <p className={styles.newCreditLimit}>
                                <FormattedMessage
                                    id="_.apply_flexi_loan.newCreditLimit"
                                    defaultMessage="Your new credit limit would be:"
                                />{' '}
                                <strong>
                                    {intlFormatCurrency({
                                        intl,
                                        value:
                                            formAmountValue + currentPrincipal
                                    })}
                                </strong>
                            </p>
                        ) : (
                            <p className={styles.newCreditLimit}>
                                <FormattedMessage
                                    id="_.apply_flexi_loan.unchangedCreditLimit"
                                    defaultMessage="Your credit limit will remain:"
                                />{' '}
                                <strong>
                                    {intlFormatCurrency({
                                        intl,
                                        value: currentCreditLimit
                                    })}
                                </strong>
                            </p>
                        ))}

                    <FormFieldSelect
                        name="purpose"
                        label={
                            isTopup ? (
                                <FormattedMessage
                                    id="_.apply_flexi_loan.topupLoanPurpose.label"
                                    defaultMessage="What is your main reason for requesting more funds?"
                                />
                            ) : (
                                <FormattedMessage
                                    id="_.apply_flexi_loan.loanPurpose.label"
                                    defaultMessage="What is your main reason for requesting a loan?"
                                />
                            )
                        }
                        placeholder={intl.formatMessage({
                            id: '_.apply_flexi_loan.loanPurpose.placeholder',
                            defaultMessage: 'Please choose an option'
                        })}
                        options={
                            getCountry() === 'DE'
                                ? loanPurposeOptionsDE
                                : loanPurposeOptions
                        }
                    />

                    <FormField
                        name="turnover"
                        label={
                            <FormattedMessage
                                id="_.apply_flexi_loan.turnover.label"
                                defaultMessage="What was your last 12 months turnover?"
                            />
                        }
                        normalize={(value: string) =>
                            parseInt(numbersOnly(value), 10)
                        }
                        format={(value: number) =>
                            value
                                ? intlFormatCurrency({
                                      intl,
                                      value
                                  })
                                : ''
                        }
                        placeholder={`${intl.formatMessage({
                            id: '_.apply_flexi_loan.turnover.placeholder',
                            defaultMessage: 'Enter turnover amount'
                        })} - ${intl.formatNumber(100000)}`}
                    />

                    {getCountry() === 'DE' && (
                        <FormFieldSelect
                            name="duration"
                            label={
                                <FormattedMessage
                                    id="_.apply_flexi_loan.loanDuration.label"
                                    defaultMessage="Wie lange benötigen Sie den Kredit?"
                                />
                            }
                            placeholder={intl.formatMessage({
                                id: '_.apply_flexi_loan.loanDuration.placeholder',
                                defaultMessage:
                                    'Bitte wählen Sie eine Option aus'
                            })}
                            options={loanDurationOptionsDE}
                        />
                    )}

                    <Button
                        className="mx-auto mt-2xl min-w-[150px]"
                        loading={createApplicationLoading}
                        type="submit"
                    >
                        <FormattedMessage
                            id="_.apply_flexi_loan.nextCTA"
                            defaultMessage="Next"
                        />
                    </Button>
                </Card.Content>
            </Card>
        </div>
    );
};

const UploadDocumentsStep = ({
    submitFailed,
    documentRequirements,
    applicationId
}: {
    submitFailed: boolean;
    applicationId: string;
    documentRequirements: TDocumentRequirements;
}) => {
    const isTopup = getQueryParameter(getCurrentLocation(), 'topup');

    useEffect(() => {
        if (isTopup) {
            tracking.ampli.topupReapplyFormDocumentsStepViewed({
                application_id: applicationId
            });
        } else {
            tracking.ampli.newLoanDocumentsStepViewed({
                application_id: applicationId
            });
        }
    }, [applicationId, isTopup]);

    return (
        <ApprovalRequirements
            submitFailed={submitFailed}
            documentRequirements={documentRequirements}
        />
    );
};
